import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Moon, Sun, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { GitHubAuthButton } from '@/components/auth/GitHubAuthButton';
import { UserMenu } from '@/components/auth/UserMenu';
import { MeshpitLogo } from '@/components/MeshpitLogo';
import {
  getCategoriesWithSubcategories,
  getNextExample,
  getPrevExample,
} from '@/examples';
import type { Example } from '@/examples/types';
import { cn } from '@/lib/utils';

export interface ExamplesToolbarProps {
  /** Currently displayed example, or undefined when no examples exist yet. */
  currentExample: Example | undefined;
  /** Called when the user selects an example from the menu or prev/next. */
  onSelectExample: (id: string) => void;
}

export function ExamplesToolbar({
  currentExample,
  onSelectExample,
}: ExamplesToolbarProps) {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();

  const categoriesWithSubcategories = getCategoriesWithSubcategories();
  const hasExamples = categoriesWithSubcategories.length > 0;

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  const handlePrev = () => {
    if (!currentExample) return;
    const prev = getPrevExample(currentExample.id);
    if (prev) {
      onSelectExample(prev.id);
      navigate(`/examples/${prev.id}`);
    }
  };

  const handleNext = () => {
    if (!currentExample) return;
    const next = getNextExample(currentExample.id);
    if (next) {
      onSelectExample(next.id);
      navigate(`/examples/${next.id}`);
    }
  };

  return (
    <div className="flex h-11 shrink-0 items-center justify-between border-b border-border bg-background px-3">
      {/* ── Left: Logo ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Link to="/" className="mr-2 flex items-center">
              <MeshpitLogo className="h-6 w-6 text-foreground" />
            </Link>
          </TooltipTrigger>
          <TooltipContent>Back to home</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="mx-1 h-5" />

        <div className="flex items-center gap-0.5 text-muted-foreground">
          <FlaskConical className="h-3.5 w-3.5" />
          <span className="hidden text-sm sm:inline">Examples</span>
        </div>
      </div>

      {/* ── Center: Prev / Nav menu / Next ────────────────────────────── */}
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handlePrev}
              disabled={!hasExamples}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Previous example</TooltipContent>
        </Tooltip>

        {hasExamples ? (
          <NavigationMenu>
            <NavigationMenuList>
              {categoriesWithSubcategories.map(({ category, subcategories }) => {
                // Is any example in this category the current one?
                const isActive = subcategories.some(({ examples }) =>
                  examples.some((e) => e.id === currentExample?.id),
                );

                return (
                  <NavigationMenuItem key={category.id}>
                    <NavigationMenuTrigger
                      className={cn(
                        'h-8 px-3 text-sm',
                        isActive && 'font-semibold',
                      )}
                    >
                      {category.label}
                    </NavigationMenuTrigger>

                    <NavigationMenuContent>
                      {/* Multi-column layout: one column per subcategory */}
                      <div
                        className="flex gap-0 p-2"
                        style={{ minWidth: `${subcategories.length * 180}px` }}
                      >
                        {subcategories.map(({ subcategory, examples }, colIdx) => (
                          <div
                            key={subcategory.id}
                            className={cn(
                              'flex w-44 flex-col',
                              colIdx > 0 && 'border-l border-border ml-1 pl-1',
                            )}
                          >
                            {/* Sub-group heading */}
                            <p className="px-2 pb-1 pt-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              {subcategory.label}
                            </p>

                            {/* Example items */}
                            {examples.map((example) => (
                              <button
                                key={example.id}
                                className={cn(
                                  'flex w-full flex-col rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                                  currentExample?.id === example.id &&
                                    'bg-accent/60 font-medium',
                                )}
                                onClick={() => {
                                  onSelectExample(example.id);
                                  navigate(`/examples/${example.id}`);
                                }}
                              >
                                <span>{example.title}</span>
                                {example.description && (
                                  <span className="text-xs leading-tight text-muted-foreground">
                                    {example.description}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                );
              })}
            </NavigationMenuList>
          </NavigationMenu>
        ) : (
          <span className="px-3 text-sm text-muted-foreground">
            No examples yet
          </span>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleNext}
              disabled={!hasExamples}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Next example</TooltipContent>
        </Tooltip>
      </div>

      {/* ── Right: current title / Theme / Auth ───────────────────────── */}
      <div className="flex items-center gap-1">
        {currentExample && (
          <>
            <span className="hidden max-w-40 truncate text-sm text-muted-foreground lg:block">
              {currentExample.title}
            </span>
            <Separator orientation="vertical" className="mx-1 hidden h-5 lg:block" />
          </>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={toggleTheme}
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}
          </TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="mx-1 h-5" />

        {isLoading ? (
          <div className="h-7 w-7 animate-pulse rounded-full bg-muted" />
        ) : isAuthenticated ? (
          <UserMenu collapsed />
        ) : (
          <GitHubAuthButton
            variant="ghost"
            size="sm"
            showText={false}
            className="h-8 w-8 p-0"
          />
        )}
      </div>
    </div>
  );
}
